import { useTranslation } from 'react-i18next';

import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { Box, Container, Group, Image, Stack } from '@optiro/ui';

import {
  PartnerClaimFormFields,
  PartnerClaimFormView,
} from '@/modules/SharedPlanning/components/PartnerClaim/PartnerClaimFormView';
import { useCreatePartnerClaimRequest, useShadowPartners } from '@/modules/SharedPlanning/hooks';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';

import logo from '../optiro.svg';

export const Route = createFileRoute('/shared/projects/$sharedProjectId/claim/form')({
  component: ClaimFormComponent,
  validateSearch: (search: Record<string, unknown>) => ({
    partnerId: Number(search.partnerId),
  }),
});

function ClaimFormComponent() {
  const { t } = useTranslation('partnerClaim');
  const { sharedProjectId } = Route.useParams();
  const { partnerId } = Route.useSearch();
  const navigate = useNavigate();
  const { isSignedIn, login } = useAuth();

  const { data: shadowPartners, isLoading } = useShadowPartners({ token: sharedProjectId });
  const partner = shadowPartners?.find((p) => p.id === partnerId);
  const mutation = useCreatePartnerClaimRequest({ token: sharedProjectId });

  const handleSubmit = (fields: PartnerClaimFormFields) => {
    if (!partner) return;
    mutation.mutate(
      { partner_id: partner.id, ...fields },
      {
        onSuccess: async () => {
          await login(fields.email, fields.password);
          navigate({ to: '/app/dashboard' });
        },
      }
    );
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'rgb(248, 245, 239)' }}>
      <Box style={{ borderBottom: '1px solid #e9ecef', backgroundColor: 'white' }}>
        <Container size="sm">
          <Group justify="center" py="md">
            <Image src={logo} alt="Optiro" h={32} w="auto" />
          </Group>
        </Container>
      </Box>

      <Container size="sm" py="xl">
        <Stack maw={400} mx="auto">
          <PartnerClaimFormView
            partner={partner}
            isLoading={isLoading}
            isSignedIn={isSignedIn}
            isSubmitting={mutation.isPending}
            errorMessage={
              mutation.isError
                ? (mutation.error?.response?.data?.error ?? t('form.genericError'))
                : null
            }
            onSubmit={handleSubmit}
          />
        </Stack>
      </Container>
    </div>
  );
}
